/**
 * The rules this device last played.
 *
 * One memory for both screens that set up a boerenbridge potje — the
 * scorekeeper's new game and a new live room — because it is one family playing
 * one set of house rules, and having to re-pick "geen blinde ronde" every single
 * evening is exactly the friction this removes.
 *
 * Its own store rather than a corner of `settings`: those are preferences of the
 * phone (sound, theme) and these are the rules of the game. And deliberately not
 * part of `scorekeeper`, because nothing in here may ever ride along in a sync
 * payload — the server decides a room's rules from what the host posts, not from
 * what a phone remembers.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { DEFAULT_HOUSE_RULES } from '@shared/types/game';
import {
  DEFAULT_HEARTS_VARIANT,
  DEFAULT_KLAVERJAS_OPTIONS,
  DEFAULT_MAX_CARDS_CAP,
  DEFAULT_SCORING_PRESET,
  SCORING_PRESETS,
  type HeartsVariant,
  type KlaverjasOptions,
} from '@shared/game-logic';
import type { GameSettingsValue } from '@/components/GameSettings';

/** What a phone that has never chosen anything starts from. */
export const DEFAULT_RULES: GameSettingsValue = {
  maxCards: DEFAULT_MAX_CARDS_CAP,
  direction: 'up-down',
  repeatTopRound: false,
  extraNoTrumpRound: true,
  noTrumpOnTheWayDown: false,
  scoringPreset: DEFAULT_SCORING_PRESET,
  customScoring: { ...SCORING_PRESETS['nl-standaard'] },
  houseRules: { ...DEFAULT_HOUSE_RULES },
};

interface RulesState {
  rules: GameSettingsValue;
  /** Klaverjassen's own options. */
  klaverjas: KlaverjasOptions;
  /** Which hartenjagen this household plays — the two are different games. */
  heartsVariant: HeartsVariant;
  /**
   * Remember what was actually started — not every keystroke.
   *
   * Called when a potje is created, so a set of options someone fiddled with and
   * then walked away from does not become the table's rules by accident.
   */
  remember: (rules: GameSettingsValue) => void;
  rememberKlaverjas: (options: KlaverjasOptions) => void;
  rememberHeartsVariant: (variant: HeartsVariant) => void;
  /** Back to the defaults, for when a stored set has drifted somewhere odd. */
  forget: () => void;
}

/**
 * Fill in anything the stored blob does not have.
 *
 * A phone that saved its rules before an option existed comes back without that
 * key, and `undefined` on a boolean would render as an unchecked switch that the
 * engine then reads as `false` — a silently different game. Same trap as the
 * missing `theme` in `settings`, so it gets the same treatment: three levels
 * deep, because the two nested objects have this problem too.
 */
function fill(stored: unknown): GameSettingsValue {
  const partial = (stored ?? {}) as Partial<GameSettingsValue>;
  return {
    ...DEFAULT_RULES,
    ...partial,
    customScoring: { ...DEFAULT_RULES.customScoring, ...partial.customScoring },
    houseRules: { ...DEFAULT_RULES.houseRules, ...partial.houseRules },
  };
}

/**
 * The same fill for klaverjassen, with `length` handled by hand: it is a union of
 * "so many deals" and "to so many points", so merging defaults into it could
 * produce an object that is neither.
 */
function fillKlaverjas(stored: unknown): KlaverjasOptions {
  const partial = (stored ?? {}) as Partial<KlaverjasOptions>;
  const length = partial.length;
  const lengthOk =
    length?.kind === 'deals'
      ? Number.isInteger(length.deals)
      : length?.kind === 'points' && Number.isInteger(length.target);
  return {
    ...DEFAULT_KLAVERJAS_OPTIONS,
    ...partial,
    length: lengthOk && length ? length : DEFAULT_KLAVERJAS_OPTIONS.length,
  };
}

export const useRules = create<RulesState>()(
  persist(
    (set) => ({
      rules: DEFAULT_RULES,
      klaverjas: DEFAULT_KLAVERJAS_OPTIONS,
      heartsVariant: DEFAULT_HEARTS_VARIANT,
      remember: (rules) => set({ rules: fill(rules) }),
      rememberKlaverjas: (options) => set({ klaverjas: fillKlaverjas(options) }),
      rememberHeartsVariant: (heartsVariant) => set({ heartsVariant }),
      forget: () =>
        set({
          rules: DEFAULT_RULES,
          klaverjas: DEFAULT_KLAVERJAS_OPTIONS,
          heartsVariant: DEFAULT_HEARTS_VARIANT,
        }),
    }),
    {
      name: 'boerenbridge.rules',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      merge: (persisted, current) => {
        const stored = persisted as
          { rules?: unknown; klaverjas?: unknown; heartsVariant?: unknown } | undefined;
        return {
          ...current,
          rules: fill(stored?.rules),
          klaverjas: fillKlaverjas(stored?.klaverjas),
          // A phone that saved before this existed has no variant; the American
          // one is what every table played until now.
          heartsVariant:
            stored?.heartsVariant === 'nederlands' ? 'nederlands' : DEFAULT_HEARTS_VARIANT,
        };
      },
    }
  )
);
